import { createContext, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { withAnalytics } from '../wrapper/withAnalytics';
import { withViewer } from '../wrapper/withViewer';
import { useEntity, useLeadCapture, useRouterPath } from './hooks';
import Loading from './preview/loading/Loading';
import Notification from './preview/notification/Notification';
import { Preview } from './preview/Preview';
import { useGlobalStore } from './state/globalStore';
import { theme } from './styles/theme';
import { DROP_DOWN_POPOVER_PORTAL_ID, ENTITY_TYPE, PREVIEW_TYPE } from './utils/constants';
import { getCookie } from './utils/helper';
import { setRemoteComponentReadyForPlayer } from './utils/player-controller';
import { UserProps } from './utils/types';
import withValidation from './utils/withValidation';

export const ThemeContext = createContext<any>({});

const LeadCaptureForm = ({ previewType, onRegistrationSuccess }: UserProps) => {
  const { setPreviewType, apiEntityId: entityId } = useGlobalStore((store) => store);

  const cookie = useGlobalStore((store) => store.cookie);
  const leadCapture = useGlobalStore((store) => store.leadCapture);
  const entityOwnerId = useGlobalStore((store) => store.entityOwnerId);
  const entityType = useGlobalStore((store) => store.entityType);
  const { onPreviewChange } = useRouterPath();

  useEffect(() => {
    if (!previewType) {
      return;
    }
    setPreviewType(previewType);
  }, [previewType, setPreviewType]);

  useEffect(() => {
    if (previewType === PREVIEW_TYPE.CONFIRMATION && !cookie) {
      onPreviewChange(PREVIEW_TYPE.FORM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookie, previewType]);

  useEffect(() => {
    const browserCookie = getCookie(entityId);
    if (!browserCookie) {
      return;
    }
    onPreviewChange(PREVIEW_TYPE.CONFIRMATION);
    onRegistrationSuccess?.(browserCookie);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (leadCapture && entityType === ENTITY_TYPE.VIDEO) {
      setRemoteComponentReadyForPlayer();
    }
  }, [leadCapture, entityType]);

  useLeadCapture();
  useEntity();

  return (
    <ThemeProvider theme={theme}>
      <PreviewWrapper>
        {leadCapture && entityOwnerId ? (
          <Preview />
        ) : (
          <LoadingWrapper>
            <Loading />
          </LoadingWrapper>
        )}
        <Notification />
      </PreviewWrapper>
      <PopOverMountPortal
        /**
         * This acts like the iris portal. Dropdown's popover needs to be rendered in a portal outside of the main, so we use this.
         */
        id={DROP_DOWN_POPOVER_PORTAL_ID}
      ></PopOverMountPortal>
    </ThemeProvider>
  );
};

export default withViewer(withAnalytics(withValidation(LeadCaptureForm)));

const PreviewWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

const LoadingWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const PopOverMountPortal = styled.div`
  position: fixed;
  display: flex;
  z-index: 200;
`;
